import { useState, useEffect } from 'react';
import liff from '@line/liff';
import { PROFILE_CACHE_DURATION } from '../constants';
import { authenticateWithLine, logout as authLogout } from '../services/auth';

export const useLiff = (liffId) => {
  const [profile, setProfile] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ローカルストレージからプロファイルを取得する関数
  const getProfileFromLocalStorage = () => {
    try {
      const cachedProfileString = localStorage.getItem('user_profile');
      if (!cachedProfileString) return null;
      
      const cachedProfile = JSON.parse(cachedProfileString);
      
      // キャッシュの有効期限をチェック
      if (cachedProfile.lastLogin) {
        const lastLoginTime = new Date(cachedProfile.lastLogin).getTime();
        const currentTime = new Date().getTime();
        
        // キャッシュが有効期限内であれば使用する
        if (currentTime - lastLoginTime < PROFILE_CACHE_DURATION) {
          console.log('Using cached profile from localStorage');
          return cachedProfile;
        } else {
          console.log('Cached profile expired, fetching new profile');
          return null;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error reading profile from localStorage', error);
      return null;
    }
  };

  // プロファイルをローカルストレージに保存する関数
  const saveProfileToLocalStorage = (profile) => {
    try {
      localStorage.setItem('user_profile', JSON.stringify({
        userId: profile.userId,
        displayName: profile.displayName || '',
        pictureUrl: profile.pictureUrl || '',
        statusMessage: profile.statusMessage || '',
        lastLogin: new Date().toISOString()
      }));
      console.log('User profile saved to localStorage');
    } catch (error) {
      console.error('Error saving profile to localStorage', error);
    }
  };

  useEffect(() => {
    const checkLiffAndGetProfile = async () => {
      try {
        // まずliffが初期化されているか確認
        if (!liff.ready) {
          // LIFF SDKが初期化されていない場合は、初期化を試みる
          // 通常はindex.jsで初期化されているはずだが、念のため
          try {
            await liff.init({ liffId });
            console.log('LIFF initialized in useLiff hook');
          } catch (initErr) {
            console.error('LIFF initialization failed in useLiff hook', initErr);
            setError('LINEの初期化に失敗しました');
            setLoading(false);
            return;
          }
        }

        // LIFFの状態をチェック
        const liffLoggedIn = liff.isLoggedIn();
        setIsLoggedIn(liffLoggedIn);

        if (liffLoggedIn) {
          // まずローカルストレージからプロファイルを取得
          const cachedProfile = getProfileFromLocalStorage();

          if (cachedProfile) {
            // キャッシュからプロフィールを設定
            setProfile(cachedProfile);
            setIsLoggedIn(true);
          } else {
            try {
              // キャッシュになければLIFF APIから取得
              const fetchedProfile = await liff.getProfile();

              // LINEアクセストークンを取得
              const accessToken = liff.getAccessToken();

              if (accessToken) {
                console.log('Authenticating with server using LINE access token...');
                // サーバー側で認証処理を実行（セッションCookie取得）
                const authResult = await authenticateWithLine(accessToken);

                console.log('Server authentication successful:', authResult);
              }

              setProfile(fetchedProfile);
              // 取得したプロフィールをキャッシュに保存
              saveProfileToLocalStorage(fetchedProfile);
              setIsLoggedIn(true);
            } catch (profileErr) {
              console.error('Failed to get profile or authenticate:', profileErr);
              setError('認証に失敗しました');
            }
          }
        }
      } catch (err) {
        console.error('Error in checkLiffAndGetProfile', err);
        setError('LINEの処理中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    checkLiffAndGetProfile();
  }, [liffId]);

  // ログイン処理 - リダイレクトURL指定可能に
  const login = (redirectUri) => {
    if (!liff.isLoggedIn()) {
      // リダイレクトURIが指定されている場合はそれを使用
      if (redirectUri) {
        liff.login({ redirectUri });
      } else {
        liff.login();
      }
    }
  };

  // ログアウト処理
  const logout = async () => {
    if (liff.isLoggedIn()) {
      try {
        // サーバー側のセッションCookieを削除
        await authLogout();
      } catch (error) {
        console.error('Server logout failed:', error);
      }

      // LIFFからログアウト
      liff.logout();
      setIsLoggedIn(false);
      setProfile(null);
      // ローカルストレージのプロファイルも削除
      localStorage.removeItem('user_profile');
      window.location.reload();
    }
  };

  // LINEアプリ内かどうかの判定
  const isInClient = () => {
    return liff.isInClient();
  };

  // シェア機能
  const shareContent = async (text) => {
    try {
      if (liff.isInClient()) {
        await liff.shareTargetPicker([
          {
            type: 'text',
            text: text
          }
        ]);
        return true;
      } else {
        // ブラウザの場合はクリップボードにコピー
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.error('Share failed', error);
      return false;
    }
  };

  // プロファイルを強制的に再取得する関数
  const refreshProfile = async () => {
    if (!liff.isLoggedIn()) {
      return null;
    }
    
    try {
      const freshProfile = await liff.getProfile();
      setProfile(freshProfile);
      saveProfileToLocalStorage(freshProfile);
      return freshProfile;
    } catch (error) {
      console.error('Failed to refresh profile', error);
      return null;
    }
  };

  return {
    profile,
    isLoggedIn,
    login,
    logout,
    loading,
    error,
    isInClient,
    shareContent,
    refreshProfile  // 新しい関数をエクスポート
  };
};